/** Karte „Psychologisches Profil“ im Drawer: Persona, Netzdiagramm der Motive, Sprachstil, Do's/Don'ts/Booster. */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CandidatePersona } from '../../campaign-wizard.types';
import { wz } from '../../components/wizard-tokens';
import { motivePolygon, personaImageUrl } from './candidate-list.logic';

const ORANGE = '#FFA502';

interface Props {
  persona: CandidatePersona | null;
  firstName: string;
}

type PersonaTab = 0 | 1 | 2 | 3;

const PersonaCard: React.FC<Props> = ({ persona, firstName }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [tab, setTab] = useState<PersonaTab>(0);
  const [open, setOpen] = useState<number>(0);

  const header = (
    <div className={classes.head}>
      <div className={classes.headTitle}>
        <PsychologyIcon sx={{ fontSize: 18, color: wz.primary }} />
        {t('wizDrawerPsychology')}
      </div>
      <div className={classes.headHint}>{t('wizDrawerPsychologyHint')}</div>
    </div>
  );

  if (!persona) {
    return (
      <div className={classes.wrap}>
        {header}
        <div className={classes.white}>
          <div className={classes.muted}>{t('wizDrawerNoPersona')}</div>
        </div>
      </div>
    );
  }

  const image = personaImageUrl(persona.image, import.meta.env.BASE_URL);
  const tabs = [
    'wizDrawerPersonaTabType',
    'wizDrawerPersonaTabDos',
    'wizDrawerPersonaTabDonts',
    'wizDrawerPersonaTabBoosters',
  ];
  const accordion = [
    { title: t('wizDrawerAccTraits', { name: firstName }), items: persona.traits },
    { title: t('wizDrawerAccDefines', { name: firstName }), items: persona.defines },
    { title: t('wizDrawerAccImportant', { name: firstName }), items: persona.important },
    { title: t('wizDrawerAccQuestions', { name: firstName }), items: persona.questions },
  ];

  return (
    <div className={classes.wrap}>
      {header}
      <div className={classes.persona}>
        {image ? (
          <img src={image} alt="Persona" className={classes.image} />
        ) : (
          <div className={classes.imageFallback}>
            {persona.name.replace(/^The\s+/i, '').slice(0, 2)}
          </div>
        )}
        <div className={classes.personaText}>
          <div className={classes.eyebrow}>{t('wizDrawerPersonality')}</div>
          <div className={classes.personaName}>{persona.name}</div>
          <div className={classes.traits}>
            {persona.traits.map(trait => (
              <div key={trait} className={classes.trait}>
                {trait}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={classes.tabs}>
        {tabs.map((key, i) => (
          <button
            key={key}
            type="button"
            className={classes.tab}
            style={{
              color: tab === i ? wz.primary : '#000',
              borderBottom: tab === i ? `2px solid ${wz.primary}` : '2px solid transparent',
            }}
            onClick={() => setTab(i as PersonaTab)}
          >
            {t(key)}
          </button>
        ))}
      </div>

      {tab === 0 ? (
        <>
          <div className={classes.chartCard}>
            <div className={classes.chartTitle}>{t('wizDrawerMotives')}</div>
            <svg viewBox="0 0 160 140" width="240" height="210" overflow="visible">
              {[46, 34.5, 23, 11.5].map(r => (
                <circle key={r} cx="80" cy="70" r={r} fill="none" stroke={wz.dashed} />
              ))}
              <line x1="80" y1="24" x2="80" y2="116" stroke={wz.dashed} />
              <line x1="34" y1="70" x2="126" y2="70" stroke={wz.dashed} />
              <polygon
                points={motivePolygon(persona.motives)}
                fill="rgba(255,165,2,0.35)"
                stroke={ORANGE}
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <text
                x="80"
                y="14"
                textAnchor="middle"
                fontSize="9"
                fill={wz.text}
                fontFamily="Roboto"
              >
                {t('wizDrawerMotiveTop')}
              </text>
              <text
                x="131"
                y="73"
                textAnchor="start"
                fontSize="9"
                fill={wz.text}
                fontFamily="Roboto"
              >
                {t('wizDrawerMotiveRight')}
              </text>
              <text
                x="80"
                y="132"
                textAnchor="middle"
                fontSize="9"
                fill={wz.text}
                fontFamily="Roboto"
              >
                {t('wizDrawerMotiveBottom')}
              </text>
              <text x="29" y="73" textAnchor="end" fontSize="9" fill={wz.text} fontFamily="Roboto">
                {t('wizDrawerMotiveLeft')}
              </text>
            </svg>
          </div>
          <div className={classes.styleCard}>
            <div className={classes.chartTitle}>{t('wizDrawerStyle')}</div>
            <div className={classes.styleBar}>
              <div
                className={classes.styleKnob}
                style={{ left: `${Math.round(persona.style * 100)}%` }}
              />
            </div>
            <div className={classes.styleLabels}>
              <div>{t('wizDrawerStyleFormal')}</div>
              <div>{t('wizDrawerStyleInformal')}</div>
            </div>
          </div>
          <div className={classes.accordion}>
            {accordion.map((a, i) => (
              <div
                key={a.title}
                className={classes.accItem}
                style={{ background: i % 2 ? '#FFF' : wz.page }}
              >
                <button
                  type="button"
                  className={classes.accHead}
                  onClick={() => setOpen(open === i ? -1 : i)}
                >
                  {a.title}
                  {open === i ? (
                    <ExpandLessIcon sx={{ fontSize: 20, color: wz.primary }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 20, color: wz.primary }} />
                  )}
                </button>
                {open === i ? (
                  <div className={classes.accBody}>
                    {a.items.map(item => (
                      <div key={item} className={classes.accRow}>
                        <span style={{ color: ORANGE }}>•</span>
                        {item}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
      {tab === 1 ? (
        <div className={classes.list}>
          {persona.dos.map(x => (
            <div key={x} className={classes.listRow}>
              <ThumbUpIcon sx={{ fontSize: 18, color: wz.success }} />
              {x}
            </div>
          ))}
        </div>
      ) : null}
      {tab === 2 ? (
        <div className={classes.list}>
          {persona.donts.map(x => (
            <div key={x} className={classes.listRow}>
              <ThumbDownIcon sx={{ fontSize: 18, color: '#F60404' }} />
              {x}
            </div>
          ))}
        </div>
      ) : null}
      {tab === 3 ? (
        <div className={classes.boosters}>
          {persona.boosters.map(x => (
            <div key={x} className={classes.booster}>
              {x}
            </div>
          ))}
        </div>
      ) : null}
      <div className={classes.footHint}>
        <AutoAwesomeIcon sx={{ fontSize: 14, color: wz.primary }} />
        {t('wizDrawerPersonaHint', { name: firstName })}
      </div>
    </div>
  );
};

const useStyles = makeStyles()({
  wrap: {
    borderRadius: 14,
    background: wz.page,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '2px 4px',
  },
  headTitle: { fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 },
  headHint: { fontSize: 12, color: wz.muted },
  white: { background: '#FFF', borderRadius: 10, padding: 12 },
  muted: { fontSize: 13, color: wz.muted },
  persona: {
    background: '#FFF',
    borderRadius: 10,
    padding: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  image: { height: 110, width: 'auto' },
  imageFallback: {
    height: 110,
    width: 110,
    borderRadius: '50%',
    background: wz.warnBg,
    color: wz.warnFg,
    fontSize: 32,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  personaText: { display: 'flex', flexDirection: 'column', gap: 8 },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: ORANGE,
  },
  personaName: { fontSize: 20, fontWeight: 700 },
  traits: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  trait: {
    fontSize: 12,
    background: wz.warnBg,
    color: wz.warnFg,
    borderRadius: 12,
    padding: '3px 9px',
  },
  tabs: {
    background: '#FFF',
    padding: 8,
    display: 'flex',
    justifyContent: 'center',
    gap: 16,
    borderRadius: 10,
  },
  tab: {
    padding: '8px 0 6px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
  },
  chartCard: {
    background: '#FFF',
    borderRadius: 10,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  chartTitle: { fontSize: 13, fontWeight: 900, textTransform: 'uppercase', textAlign: 'center' },
  styleCard: {
    background: '#FFF',
    borderRadius: 10,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  styleBar: {
    position: 'relative',
    height: 8,
    borderRadius: 999,
    background: `linear-gradient(to right,${wz.border},${wz.warnBg})`,
  },
  styleKnob: {
    position: 'absolute',
    top: -4,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: ORANGE,
    border: '2px solid #FFF',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    transform: 'translateX(-50%)',
  },
  styleLabels: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: wz.muted },
  accordion: { background: '#FFF', borderRadius: 10, overflow: 'hidden', border: '1px solid #ddd' },
  accItem: { borderBottom: '1px solid #ddd' },
  accHead: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    textAlign: 'left',
    color: wz.text,
  },
  accBody: { padding: '0 12px 10px', display: 'flex', flexDirection: 'column', gap: 4 },
  accRow: { fontSize: 13, color: wz.textDim, display: 'flex', gap: 6 },
  list: {
    background: '#FFF',
    borderRadius: 10,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  listRow: { display: 'flex', gap: 8, fontSize: 13 },
  boosters: {
    background: '#FFF',
    borderRadius: 10,
    padding: 14,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
  },
  booster: {
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 16,
    padding: '5px 12px',
    background: wz.warnBg,
    color: wz.warnFg,
  },
  footHint: {
    fontSize: 12,
    color: wz.muted,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '0 4px',
  },
});

export default PersonaCard;
